import { css } from "@emotion/css";
import { LinearProgress, PopoverBasicHelp } from "@octopusdeploy/design-system-components";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { LicenseLimitUsage, LicenseUsageResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { Loading } from "~/components/Loading/Loading";
import { getKnownLicenseUsageNames } from "./KnownLicenseUsageName";
import { getLicenseUsageContextualHelp } from "./getLicenseUsageContextualHelp";
const displayLimit = getKnownLicenseUsageNames();
interface LicenseUsageOverviewProps {
    licenseUsage?: LicenseUsageResource | null;
}
export const LicenseUsageOverview = ({ licenseUsage }: LicenseUsageOverviewProps) => {
    if (!licenseUsage || !licenseUsage.Limits) {
        return <Loading busy={true}/>;
    }
    const filteredAndOrderedUsageOverviews = displayLimit.map((limitName) => licenseUsage.Limits.find((l) => l.Name.toLowerCase() === limitName && !l.IsUnlimited)).filter((item) => item !== undefined);
    return (<div className={styles.container}>
            <div className={styles.title}>License Usage Overview</div>
            <div className={styles.grid}>
                {filteredAndOrderedUsageOverviews.map((limit, index) => (<LicenseLimitOverview key={index} limit={limit} contextualHelp={getLicenseUsageContextualHelp(limit.Name)}/>))}
            </div>
        </div>);
};
function LicenseLimitOverview({ limit, contextualHelp }: {
    limit: LicenseLimitUsage;
    contextualHelp?: React.ReactNode;
}) {
    const linearProgressValue = Math.min(limit.CurrentUsage / limit.LicensedLimit, 1) * 100;
    return (<React.Fragment>
            <div className={styles.nameColumn}>
                {limit.Name} {contextualHelp && <PopoverBasicHelp description={contextualHelp} placement={"right-start"}/>}
            </div>
            <div className={styles.usageColumn}>
                {limit.CurrentUsage} / {limit.LicensedLimit}
            </div>
            <div className={styles.progressColumn}>
                <LinearProgress show value={linearProgressValue} variant="determinate"/>
            </div>
        </React.Fragment>);
}
const styles = {
    container: css({
        padding: space[16],
        borderRadius: borderRadius.medium,
        backgroundColor: themeTokens.color.dialog.background.secondary.default,
    }),
    title: css({
        textTransform: "uppercase",
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.secondary,
        marginBottom: space[12],
    }),
    grid: css({
        display: "grid",
        gridTemplateColumns: "auto auto 1fr", // Auto-size text columns, progress stretches
        columnGap: space[12],
        rowGap: space[16],
        alignItems: "center",
    }),
    nameColumn: css({
        font: text.body.regular.medium,
        color: themeTokens.color.text.primary,
    }),
    usageColumn: css({
        font: text.body.regular.medium,
        color: themeTokens.color.text.primary,
        textAlign: "right",
        minWidth: "70px", // Ensure enough space for usage text
    }),
    progressColumn: css({
        // Progress bar takes full width of its column
        width: "100%",
        // Ensure container has rounded ends and the progress bar cannot escape the container's bounds
        borderRadius: borderRadius.circle,
        overflow: "hidden",
    }),
};
